import { Controller } from "@hotwired/stimulus"

// Comunicación bidireccional entre el editor (parent) y el iframe
// preview del showcase para selección de secciones e items.
//
// Replica la interacción de AdminEditSection.tsx + WebsiteEditorSections.tsx
// del frontend React, adaptada a la arquitectura de iframe cross-origin
// con postMessage.
//
// Flujo preview → sidebar:
//   Click en section/item en el iframe
//   → JS inline en el iframe envía postMessage (SELECT_SECTION/SELECT_ITEM)
//   → Este controller recibe el mensaje
//   → Navega el turbo_frame "editor_aside_content" al edit URL
//
// Flujo sidebar → preview:
//   Click en fila de section/item en el sidebar
//   → data-action dispara scrollToSection/scrollToItem
//   → Este controller envía postMessage (SCROLL_TO) al iframe
//   → JS inline en el iframe scrollea y resalta el elemento
//
// Valores:
//   data-manage--editor-selection-base-url-value — URL base del editor
//     (ej: "/manage/alvaro/admin/pages/website_pages/config/2225")

export default class extends Controller {
  static targets = ["iframe"]
  static values = { baseUrl: String }

  #onMessage = null

  connect() {
    this.#onMessage = this.#handleMessage.bind(this)
    window.addEventListener("message", this.#onMessage)
  }

  disconnect() {
    window.removeEventListener("message", this.#onMessage)
  }

  // Action: click en fila de seccion del sidebar → scroll preview
  scrollToSection(event) {
    const sectionId = event.currentTarget.dataset.sectionId
      || event.params?.sectionId
    if (!sectionId) return
    this.#postToIframe({ type: "SCROLL_TO", target: `page_section_${sectionId}` })
  }

  // Action: click en fila de item del sidebar → scroll preview
  scrollToItem(event) {
    const itemId = event.currentTarget.dataset.itemId
      || event.params?.itemId
    if (!itemId) return
    this.#postToIframe({ type: "SCROLL_TO", target: `page_item_${itemId}` })
  }

  // --- Private ---

  #handleMessage(event) {
    const data = event.data
    if (!data || !data.type) return

    if (data.type === "SELECT_SECTION" && data.sectionId) {
      this.#navigateToSection(data.sectionId)
    }

    if (data.type === "SELECT_ITEM" && data.itemId && data.sectionId) {
      this.#navigateToItem(data.sectionId, data.itemId)
    }

    // Inline edit en el iframe persistio un cambio (showcase/page/editor_selection
    // dispara `RECORD_SAVED` tras un PATCH exitoso del body). Re-disparamos el
    // evento global que escucha `manage--editor-draft-store` para marcar dirty
    // → habilita "Guardar borrador" + actualiza "Borrador guardado".
    if (data.type === "RECORD_SAVED") {
      document.dispatchEvent(new CustomEvent("editor:record-saved", { bubbles: true }))
    }
  }

  #navigateToSection(sectionId) {
    const url = `${this.baseUrlValue}/sections/${sectionId}/edit`
    this.#navigateFrame(url)
  }

  #navigateToItem(sectionId, itemId) {
    const url = `${this.baseUrlValue}/sections/${sectionId}/items/${itemId}/edit`
    this.#navigateFrame(url)
  }

  #navigateFrame(url) {
    const frame = document.getElementById("editor_aside_content")
    if (frame) {
      frame.src = url
    }
  }

  #postToIframe(data) {
    if (!this.hasIframeTarget) return
    const iframe = this.iframeTarget
    if (iframe.contentWindow) {
      iframe.contentWindow.postMessage(data, "*")
    }
  }
}
